import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useAssignAsset } from "../hooks/useAssets";

type Mode = "person" | "project";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  assetId: string;
  assetName: string;
  onAssigned?: (name: string) => void;
};

export function AssignAssetModal({
  isOpen,
  onClose,
  assetId,
  assetName,
  onAssigned,
}: Props) {
  const assignMutation = useAssignAsset(assetId);
  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 200,
    bypassScope: true,
  });
  const users = usersResult?.data ?? [];
  const projects = projectsResult?.data ?? [];

  const [mode, setMode] = useState<Mode>("person");
  const [assignedToId, setAssignedToId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [assignedAt, setAssignedAt] = useState(
    () => new Date().toISOString().slice(0, 10),
  );
  const [expectedReturn, setExpectedReturn] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setAssignedToId("");
      setProjectId("");
      setAssignedAt(new Date().toISOString().slice(0, 10));
      setExpectedReturn("");
      setNotes("");
      setError(null);
      setMode("person");
    }
  }, [isOpen, assetId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!assignedToId && !projectId) {
      setError("Select a user or project");
      return;
    }
    setError(null);
    try {
      const res = await assignMutation.mutateAsync({
        assignedToId: assignedToId || null,
        projectId: projectId || null,
        assignedAt: assignedAt
          ? new Date(assignedAt).toISOString()
          : null,
        expectedReturn: expectedReturn
          ? new Date(expectedReturn).toISOString()
          : null,
        notes: notes.trim() || null,
      });
      const assignedName =
        res.asset.assignedToName ??
        res.asset.projectName ??
        "selected target";
      onAssigned?.(assignedName);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Assignment failed");
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/30"
        onClick={onClose}
      >
        <div
          className="w-[480px] max-w-[95vw] overflow-hidden rounded-xl border bg-white shadow-xl"
          style={{ borderColor: "hsl(210,30%,85%)" }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="flex items-center justify-between border-b px-5 py-3"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground">
                Assign Asset
              </h2>
              <div className="truncate text-[12px] text-muted-foreground">
                {assetName}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 p-5">
            <div
              className="inline-flex rounded-md border bg-muted p-0.5"
              style={{ borderColor: "hsl(210,30%,85%)" }}
            >
              <button
                type="button"
                onClick={() => setMode("person")}
                className={`px-3 py-1.5 text-xs font-medium rounded ${
                  mode === "person"
                    ? "bg-white text-foreground shadow-sm"
                    : "text-muted-foreground"
                }`}
              >
                Person
              </button>
              <button
                type="button"
                onClick={() => setMode("project")}
                className={`px-3 py-1.5 text-xs font-medium rounded ${
                  mode === "project"
                    ? "bg-white text-foreground shadow-sm"
                    : "text-muted-foreground"
                }`}
              >
                Project
              </button>
            </div>

            {mode === "person" && (
              <div className="space-y-2">
                <Label>Assign to user</Label>
                <Select
                  value={assignedToId}
                  onChange={(e) => setAssignedToId(e.target.value)}
                >
                  <option value="">— Select user —</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {mode === "project" && (
              <div className="space-y-2">
                <Label>Assign to project</Label>
                <Select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                >
                  <option value="">— Select project —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.code} | {p.name}
                      {p.companyName ? ` · ${p.companyName}` : ""}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {(assignedToId || projectId) && (
              <div className="space-y-2">
                <Label>Additional target (optional)</Label>
                {mode === "person" ? (
                  <Select
                    value={projectId}
                    onChange={(e) => setProjectId(e.target.value)}
                  >
                    <option value="">No project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.code} | {p.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Select
                    value={assignedToId}
                    onChange={(e) => setAssignedToId(e.target.value)}
                  >
                    <option value="">No specific user</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Select>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Assigned Date</Label>
                <Input
                  type="date"
                  value={assignedAt}
                  onChange={(e) => setAssignedAt(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Expected Return</Label>
                <Input
                  type="date"
                  value={expectedReturn}
                  onChange={(e) => setExpectedReturn(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              />
            </div>

            {error && (
              <div className="rounded-md border bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {error}
              </div>
            )}

            <div
              className="-mx-5 -mb-5 flex items-center justify-end gap-2 border-t bg-[hsl(210,30%,98%)] px-5 py-3"
              style={{ borderColor: "hsl(210,30%,90%)" }}
            >
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={assignMutation.isPending}>
                {assignMutation.isPending ? "Assigning…" : "Assign Asset"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}
